/**
 * 企业资产库管理器
 * @author Joey
 */
import { useToast } from "@/hooks/use-toast";
import { canUseWindow } from "@/utils/canuseWindow";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserManagementHooks } from "./agent-dashboard-user-management-manager";
import { agentUserGlobalTemplateAdd, GlobalTemplateAddRequest } from "@/services/agent-user-global-template-add";
import { useTranslation } from "react-i18next";
import { agentUserGlobalTemplatesList, AgentUserGlobalTemplatesListRequest, AssetsTemplatesData } from "@/services/agent-user-global-template-list";
import { toggleAgentUserGlobalTemplateStatus } from "@/services/agent-user-global-template-toggle-status";
import { deleteAgentUserGlobalTemplate } from "@/services/agent-user-global-template-delet";
import { adminVideoTemplateList, AdminVideoTemplateListItemData } from "@/services/admin-video-template-list";
import { adminPostTemplateList, AdminPostTemplateListItemData } from "@/services/admin-post-template-list";
import { adminArticleTemplateList, AdminTemplateListItemData } from "@/services/admin-article-template-list";

// 缓存数据结构
export interface SearchFormData {
  searchType: string;
  searchValue: string;
  /** 1 短视频; 4 图文; 5 文章*/
  type: number;
  page: number;
  pageSize: number;
  total?: number;
}

export interface AdminVideoTemplateListSearchForm {
  page: number;
  pageSize: number;
  total: number;
  templateId: string;
}

export interface AdminPostTemplateListSearchForm {
  page: number;
  pageSize: number;
  total: number;
  templateId: string;
}

export interface AdminArticleTemplateListSearchForm {
  page: number;
  pageSize: number;
  total: number;
  templateId: string;
}

type TemplatesChangeEventDetail = {
  templates: AssetsTemplatesData[];
};
type TemplatesChangeEvent = CustomEvent<TemplatesChangeEventDetail>;

type SearchFormChangeEventDetail = SearchFormData;
type SearchFormChangeEvent = CustomEvent<SearchFormChangeEventDetail>;

type TotalTemplateCountChangeEventDetail = number;
type TotalTemplateCountChangeEvent = CustomEvent<TotalTemplateCountChangeEventDetail>;

type AdminVideoTemplatesChangeEventDetail = AdminVideoTemplateListItemData[];
type AdminVideoTemplatesChangeEvent = CustomEvent<AdminVideoTemplatesChangeEventDetail>;

type AdminVideoTemplatesSearchFormChangeEventDetail = AdminVideoTemplateListSearchForm;
type AdminVideoTemplatesSearchFormChangeEvent = CustomEvent<AdminVideoTemplatesSearchFormChangeEventDetail>;

type AdminPostTemplatesChangeEventDetail = AdminPostTemplateListItemData[];
type AdminPostTemplatesChangeEvent = CustomEvent<AdminPostTemplatesChangeEventDetail>;

type AdminPostTemplatesSearchFormChangeEventDetail = AdminPostTemplateListSearchForm;
type AdminPostTemplatesSearchFormChangeEvent = CustomEvent<AdminPostTemplatesSearchFormChangeEventDetail>;

type AdminArticleTemplatesChangeEventDetail = AdminTemplateListItemData[];
type AdminArticleTemplatesChangeEvent = CustomEvent<AdminArticleTemplatesChangeEventDetail>;

type AdminArticleTemplatesSearchFormChangeEventDetail = AdminArticleTemplateListSearchForm;
type AdminArticleTemplatesSearchFormChangeEvent = CustomEvent<AdminArticleTemplatesSearchFormChangeEventDetail>;

class AgentCompanyAssetApi {
  constructor() { }
  // 请求模板列表
  async fetchTemplatesList(params: AgentUserGlobalTemplatesListRequest) {
    return await agentUserGlobalTemplatesList(params);
  }
  // 添加模板
  async addTemplates(params: GlobalTemplateAddRequest[]) {
    return await agentUserGlobalTemplateAdd(params);
  }
  // 启用/禁用模板
  async toggleTemplateStatus(id: number) {
    return await toggleAgentUserGlobalTemplateStatus({ id });
  }
  // 删除模板
  async deleteTemplates(id: number[]) {
    return await deleteAgentUserGlobalTemplate(id);
  }
}

export class AgentCompanyAssetManager {
  private static instance: AgentCompanyAssetManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_COMPANY_ASSETS_TEMPLATES_LIST";
  private api: AgentCompanyAssetApi;
  // 当前智能体ID
  private agentId: string = "";
  // 智能体内容列表
  private templates: AssetsTemplatesData[] = [];
  // 可选的总模板数量(模板选择弹窗里面的模板数量)
  private totalTemplateCount: number = 0;
  // 搜索表单的数据
  private searchForm: SearchFormData = {
    searchType: "",
    searchValue: "",
    type: 1,
    page: 1,
    pageSize: 1000,
    total: 0,
  }
  // 后台视频模板列表
  private adminVideoTemplates: AdminVideoTemplateListItemData[] = [];
  // 后台视频模板搜索表单的数据
  private adminVideoTemplatesSearchForm: AdminVideoTemplateListSearchForm = {
    page: 1,
    pageSize: 24,
    total: 0,
    templateId: "",
  }
  // 后台图文模板列表
  private adminPostTemplates: AdminPostTemplateListItemData[] = [];
  // 后台图文模板搜索表单
  private adminPostTemplatesSearchForm: AdminPostTemplateListSearchForm = {
    page: 1,
    pageSize: 24,
    total: 0,
    templateId: "",
  };
  // 后台文章模板列表
  private adminArticleTemplates: AdminTemplateListItemData[] = [];
  // 后台文章模板搜索表单
  private adminArticleTemplatesSearchForm: AdminArticleTemplateListSearchForm = {
    page: 1,
    pageSize: 24,
    total: 0,
    templateId: "",
  };

  private constructor() {
    this.api = new AgentCompanyAssetApi();
  }

  // 单例模式
  public static getInstance(): AgentCompanyAssetManager {
    if (!AgentCompanyAssetManager.instance) {
      AgentCompanyAssetManager.instance = new AgentCompanyAssetManager();
    }
    return AgentCompanyAssetManager.instance;
  }

  // 初始化
  public async init(agentId: string, refresh: boolean = false) {
    this.setAgentId(agentId);
    try {
      // 初始化时，如果列表为空，才请求数据
      if (this.getTemplates().length === 0 || refresh) {
        await this.fetchTemplates();
      }
    } catch (error) {
      console.error("Failed to init agent company assets templates cache:", error);
      throw error;
    }
  }

  // 请求列表数据
  public async fetchTemplates(page?: number) {
    try {
      const data = await this.api.fetchTemplatesList({
        agentId: this.agentId,
        ...this.getSearchForm(),
      });
      this.setTemplates(data.list);
      this.setSearchForm({
        ...this.searchForm,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
      });
      this.setTotalTemplateCount(data.totalTemplateCount);
    } catch (error) {
      console.error("Failed to fetch agent company assets templates list:", error);
      this.dispatchTemplatesChange();
      throw error;
    }
  }

  // 添加模板
  public async addTemplate(templateList: GlobalTemplateAddRequest[]) {
    try {
      return await this.api.addTemplates(templateList);
    } catch (error) {
      console.error("Failed to add agent company asset template:", error);
      throw error;
    }
  }

  // 启用/禁用模板
  public async toggleTemplateStatus(id: number) {
    const _templates = [...this.getTemplates()];
    try {
      const res = await this.api.toggleTemplateStatus(id);
      const newTemplate = _templates.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            status: res.status,
          }
        }
        return item;
      })
      this.setTemplates(newTemplate);
      return res;
    } catch (error) {
      console.error("Failed to toggle agent company asset template status:", error);
      // 失败回滚
      this.setTemplates(_templates);
      throw error;
    }
  }

  // 删除模板
  public async deleteTemplates(id: number[]) {
    const _templates = [...this.getTemplates()];
    try {
      await this.api.deleteTemplates(id);
      const newTemplates = _templates.filter((item) => !id.includes(item.id));
      this.setTemplates(newTemplates);
    } catch (error) {
      console.error("Failed to delete agent company asset template:", error);
      // 失败回滚
      this.setTemplates(_templates);
      throw error;
    }
  }

  // 重置列表和搜索表单
  public resetTemplatesAndSearchForm() {
    this.setTemplates([]);
    this.setSearchForm({
      searchType: '',
      searchValue: '',
      type: 1,
      page: 1,
      pageSize: 1000,
      total: 0,
    });
  }

  // 请求后台视频模板列表；append 为 true 时请求下一页并拼接到当前列表
  public async fetchAdminVideoTemplates(options?: { append?: boolean; agentId?: string }) {
    const append = options?.append === true;
    const form = this.adminVideoTemplatesSearchForm;
    const requestPage = append ? form.page + 1 : form.page;
    try {
      const data = await adminVideoTemplateList({
        page: requestPage,
        pageSize: form.pageSize,
        filter: {
          templateId: form.templateId,
          agentId: options?.agentId,
        },
      });
      if (append) {
        if (data.list.length === 0) {
          this.setAdminVideoTemplatesSearchForm({
            ...this.adminVideoTemplatesSearchForm,
            total: data.count,
          });
          return;
        }
        this.setAdminVideoTemplates([...this.adminVideoTemplates, ...data.list]);
        this.setAdminVideoTemplatesSearchForm({
          ...this.adminVideoTemplatesSearchForm,
          page: requestPage,
          total: data.count,
        });
      } else {
        this.setAdminVideoTemplates(data.list);
        this.setAdminVideoTemplatesSearchForm({
          ...this.adminVideoTemplatesSearchForm,
          page: requestPage,
          total: data.count,
        });
      }
    } catch (error) {
      console.error("Failed to fetch admin video templates list:", error);
      this.dispatchAdminVideoTemplatesChange();
      throw error;
    }
  }

  // 请求后台图文模板列表；append 为 true 时请求下一页并拼接到当前列表
  public async fetchAdminPostTemplates(options?: { append?: boolean; agentId?: string }) {
    const append = options?.append === true;
    const form = this.adminPostTemplatesSearchForm;
    const requestPage = append ? form.page + 1 : form.page;
    try {
      const data = await adminPostTemplateList({
        page: requestPage,
        pageSize: form.pageSize,
        filter: {
          templateId: form.templateId,
          agentId: options?.agentId,
        },
      });
      if (append) {
        if (data.list.length === 0) {
          this.setAdminPostTemplatesSearchForm({
            ...this.adminPostTemplatesSearchForm,
            total: data.count,
          });
          return;
        }
        this.setAdminPostTemplates([...this.adminPostTemplates, ...data.list]);
        this.setAdminPostTemplatesSearchForm({
          ...this.adminPostTemplatesSearchForm,
          page: requestPage,
          total: data.count,
        });
      } else {
        this.setAdminPostTemplates(data.list);
        this.setAdminPostTemplatesSearchForm({
          ...this.adminPostTemplatesSearchForm,
          page: requestPage,
          total: data.count,
        });
      }
    } catch (error) {
      console.error("Failed to fetch admin post templates list:", error);
      this.dispatchAdminPostTemplatesChange();
      throw error;
    }
  }

  // 请求后台文章模板列表；append 为 true 时请求下一页并拼接到当前列表
  public async fetchAdminArticleTemplates(options?: { append?: boolean; agentId?: string }) {
    const append = options?.append === true;
    const form = this.adminArticleTemplatesSearchForm;
    const requestPage = append ? form.page + 1 : form.page;
    try {
      const data = await adminArticleTemplateList({
        page: requestPage,
        pageSize: form.pageSize,
        filter: {
          templateId: form.templateId,
          agentId: options?.agentId,
        },
      });
      if (append) {
        if (data.list.length === 0) {
          this.setAdminArticleTemplatesSearchForm({
            ...this.adminArticleTemplatesSearchForm,
            total: data.count,
          });
          return;
        }
        this.setAdminArticleTemplates([...this.adminArticleTemplates, ...data.list]);
        this.setAdminArticleTemplatesSearchForm({
          ...this.adminArticleTemplatesSearchForm,
          page: requestPage,
          total: data.count,
        });
      } else {
        this.setAdminArticleTemplates(data.list);
        this.setAdminArticleTemplatesSearchForm({
          ...this.adminArticleTemplatesSearchForm,
          page: requestPage,
          total: data.count,
        });
      }
    } catch (error) {
      console.error("Failed to fetch admin article templates list:", error);
      this.dispatchAdminArticleTemplatesChange();
      throw error;
    }
  }

  // ==================== 读取写入操作 ====================
  // 企业资产库列表
  public getTemplates() {
    return this.templates;
  }
  private setTemplates(templates: AssetsTemplatesData[]) {
    this.templates = templates;
    this.dispatchTemplatesChange();
  }

  public getAgentId() {
    return this.agentId;
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }

  public getSearchForm() {
    return this.searchForm;
  }
  public setSearchForm(form: SearchFormData) {
    this.searchForm = form;
    this.dispatchSearchFormChange();
  }

  // 可选的总模板数量(模板选择弹窗里面的模板数量)
  public getTotalTemplateCount() {
    return this.totalTemplateCount;
  }
  private setTotalTemplateCount(count: number) {
    this.totalTemplateCount = count;
    this.dispatchTotalTemplateCountChange();
  }

  // 后台视频模板列表
  public getAdminVideoTemplates() {
    return this.adminVideoTemplates;
  }
  private setAdminVideoTemplates(templates: AdminVideoTemplateListItemData[]) {
    this.adminVideoTemplates = templates;
    this.dispatchAdminVideoTemplatesChange();
  }

  // 后台视频模板搜索表单的数据
  public getAdminVideoTemplatesSearchForm() {
    return this.adminVideoTemplatesSearchForm;
  }
  public setAdminVideoTemplatesSearchForm(form: AdminVideoTemplateListSearchForm) {
    this.adminVideoTemplatesSearchForm = form;
    this.dispatchAdminVideoTemplatesSearchFormChange();
  }

  // 后台图文模板列表
  public getAdminPostTemplates() {
    return this.adminPostTemplates;
  }
  private setAdminPostTemplates(templates: AdminPostTemplateListItemData[]) {
    this.adminPostTemplates = templates;
    this.dispatchAdminPostTemplatesChange();
  }

  public getAdminPostTemplatesSearchForm() {
    return this.adminPostTemplatesSearchForm;
  }
  public setAdminPostTemplatesSearchForm(form: AdminPostTemplateListSearchForm) {
    this.adminPostTemplatesSearchForm = form;
    this.dispatchAdminPostTemplatesSearchFormChange();
  }

  // 后台文章模板列表
  public getAdminArticleTemplates() {
    return this.adminArticleTemplates;
  }
  private setAdminArticleTemplates(templates: AdminTemplateListItemData[]) {
    this.adminArticleTemplates = templates;
    this.dispatchAdminArticleTemplatesChange();
  }

  public getAdminArticleTemplatesSearchForm() {
    return this.adminArticleTemplatesSearchForm;
  }
  public setAdminArticleTemplatesSearchForm(form: AdminArticleTemplateListSearchForm) {
    this.adminArticleTemplatesSearchForm = form;
    this.dispatchAdminArticleTemplatesSearchFormChange();
  }

  // ==================== 订阅通知操作 ====================
  // 监听列表变化事件
  private dispatchTemplatesChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<TemplatesChangeEventDetail>(`${this.STORAGE_KEY}:templates-change`, {
      detail: { templates: this.templates }
    });
    window.dispatchEvent(event);
  }
  public onTemplatesChange(listener: (e: TemplatesChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:templates-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:templates-change`, listener);
  }

  // 监听搜索表单变化事件
  private dispatchSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<SearchFormChangeEventDetail>(`${this.STORAGE_KEY}:search-form-change`, {
      detail: this.searchForm
    });
    window.dispatchEvent(event);
  }
  public onSearchFormChange(listener: (e: SearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:search-form-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:search-form-change`, listener);
  }

  // 监听可选的总模板数量变化事件
  private dispatchTotalTemplateCountChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<TotalTemplateCountChangeEventDetail>(`${this.STORAGE_KEY}:total-template-count-change`, {
      detail: this.totalTemplateCount
    });
    window.dispatchEvent(event);
  }
  public onTotalTemplateCountChange(listener: (e: TotalTemplateCountChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:total-template-count-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:total-template-count-change`, listener);
  }

  // 监听后台视频模板列表变化事件
  private dispatchAdminVideoTemplatesChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AdminVideoTemplatesChangeEventDetail>(`${this.STORAGE_KEY}:admin-video-templates-change`, {
      detail: this.adminVideoTemplates
    });
    window.dispatchEvent(event);
  }
  public onAdminVideoTemplatesChange(listener: (e: AdminVideoTemplatesChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:admin-video-templates-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:admin-video-templates-change`, listener);
  }

  // 监听后台视频模板搜索表单变化事件
  private dispatchAdminVideoTemplatesSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AdminVideoTemplatesSearchFormChangeEventDetail>(`${this.STORAGE_KEY}:admin-video-templates-search-form-change`, {
      detail: this.adminVideoTemplatesSearchForm
    });
    window.dispatchEvent(event);
  }
  public onAdminVideoTemplatesSearchFormChange(listener: (e: AdminVideoTemplatesSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:admin-video-templates-search-form-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:admin-video-templates-search-form-change`, listener);
  }

  private dispatchAdminPostTemplatesChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AdminPostTemplatesChangeEventDetail>(`${this.STORAGE_KEY}:admin-post-templates-change`, {
      detail: this.adminPostTemplates
    });
    window.dispatchEvent(event);
  }
  public onAdminPostTemplatesChange(listener: (e: AdminPostTemplatesChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:admin-post-templates-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:admin-post-templates-change`, listener);
  }

  private dispatchAdminPostTemplatesSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AdminPostTemplatesSearchFormChangeEventDetail>(`${this.STORAGE_KEY}:admin-post-templates-search-form-change`, {
      detail: this.adminPostTemplatesSearchForm
    });
    window.dispatchEvent(event);
  }
  public onAdminPostTemplatesSearchFormChange(listener: (e: AdminPostTemplatesSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:admin-post-templates-search-form-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:admin-post-templates-search-form-change`, listener);
  }

  private dispatchAdminArticleTemplatesChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AdminArticleTemplatesChangeEventDetail>(`${this.STORAGE_KEY}:admin-article-templates-change`, {
      detail: this.adminArticleTemplates
    });
    window.dispatchEvent(event);
  }
  public onAdminArticleTemplatesChange(listener: (e: AdminArticleTemplatesChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:admin-article-templates-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:admin-article-templates-change`, listener);
  }

  private dispatchAdminArticleTemplatesSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AdminArticleTemplatesSearchFormChangeEventDetail>(`${this.STORAGE_KEY}:admin-article-templates-search-form-change`, {
      detail: this.adminArticleTemplatesSearchForm
    });
    window.dispatchEvent(event);
  }
  public onAdminArticleTemplatesSearchFormChange(listener: (e: AdminArticleTemplatesSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(`${this.STORAGE_KEY}:admin-article-templates-search-form-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:admin-article-templates-search-form-change`, listener);
  }
}

// ============= Hooks =============
// 企业资产库列表数据
const useTemplatesData = (refresh?: boolean, enabledInit = true) => {
  const toast = useToast();
  const { id } = useParams();

  const [templates, setTemplates] = useState(agentCompanyAssetManager.getTemplates());
  const [searchFormData, setSearchFormData] = useState(agentCompanyAssetManager.getSearchForm());
  const [totalTemplateCount, setTotalTemplateCount] = useState(agentCompanyAssetManager.getTotalTemplateCount());

  const [loading, setLoading] = useState(false);

  useUserManagementHooks.useListenerAfterAction('company-asset');

  const init = async () => {
    if (!id) {
      console.error('Agent id is required');
      return;
    }
    if (!enabledInit) return;
    agentCompanyAssetManager.setAgentId(id);
    try {
      setLoading(true);
      await agentCompanyAssetManager.init(id, refresh);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const search = async (page?: number) => {
    setLoading(true);
    try {
      await agentCompanyAssetManager.fetchTemplates(page);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  const changeSearchForm = (key: keyof SearchFormData, value: string | number) => {
    agentCompanyAssetManager.setSearchForm({
      ...searchFormData,
      [key]: value,
    });
  }

  const changePage = (page: number) => {
    changeSearchForm('page', page);
  }

  const resetAndSearch = async () => {
    agentCompanyAssetManager.setSearchForm({
      ...searchFormData,
      searchType: "",
      searchValue: "",
      page: 1,
      pageSize: 1000,
      total: 0,
    });
    await search();
  }

  useEffect(() => {
    init();
  }, [id])

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onTemplatesChange((e) => {
      setTemplates(e.detail.templates)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onSearchFormChange((e) => {
      setSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onTotalTemplateCountChange((e) => {
      setTotalTemplateCount(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    templates,
    searchFormData,
    totalTemplateCount,
    loading,
    search,
    changePage,
    resetAndSearch,
    changeSearchForm,
  }
}

// 添加模板
const useTemplateAdd = () => {
  const toast = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const handleAddTemplate = async (templateList: Omit<GlobalTemplateAddRequest, 'agentId' | 'type'>[]) => {
    setLoading(true);
    try {
      const params = templateList.map((item) => ({
        ...item,
        agentId: agentCompanyAssetManager.getAgentId(),
        type: agentCompanyAssetManager.getSearchForm().type,
      }));

      const res = await agentCompanyAssetManager.addTemplate(params);

      if (res.results.some((item) => item.successFlag)) {
        // 刷新列表
        agentCompanyAssetManager.fetchTemplates();
        toast.toast({
          description: t('agent_dashboard.company_asset.add_success'),
        });
        return
      } else {
        toast.toast({
          description: t('agent_dashboard.company_asset.add_failed'),
        });
      }
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    handleAddTemplate,
  }
}

// 启用/禁用模板
const useTemplateToggleStatus = () => {
  const toast = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const handleToggleTemplateStatus = async (id: number) => {
    setLoading(true);
    try {
      const result = await agentCompanyAssetManager.toggleTemplateStatus(id);

      toast.toast({
        description: result.status === 1 ? t('agent_dashboard.company_asset.enable_success') : t('agent_dashboard.company_asset.disable_success'),
      });
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    handleToggleTemplateStatus,
  }
}

// 删除模板
const useTemplateDelete = () => {
  const toast = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const handleDeleteTemplate = async (id: number[]) => {
    setLoading(true);
    try {
      await agentCompanyAssetManager.deleteTemplates(id);
      toast.toast({
        description: t('agent_dashboard.company_asset.delete_success'),
      });
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    handleDeleteTemplate,
  }
}

// 后台视频模板列表数据
// agentId 仅在需要按智能体过滤时传入（如用户详情页的模板新增弹窗），其他场景不传
const useAdminVideoTemplatesData = (agentId?: string) => {
  const toast = useToast();
  const [adminVideoTemplates, setAdminVideoTemplates] = useState(agentCompanyAssetManager.getAdminVideoTemplates());
  const [adminVideoTemplatesSearchForm, setAdminVideoTemplatesSearchForm] = useState(agentCompanyAssetManager.getAdminVideoTemplatesSearchForm());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const agentIdRef = useRef(agentId);
  loadingRef.current = loading;
  loadingMoreRef.current = loadingMore;
  agentIdRef.current = agentId;

  const search = async () => {
    try {
      setLoading(true);
      await agentCompanyAssetManager.fetchAdminVideoTemplates({
        append: false,
        agentId: agentIdRef.current,
      });
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const loadMore = useCallback(async () => {
    const form = agentCompanyAssetManager.getAdminVideoTemplatesSearchForm();
    const list = agentCompanyAssetManager.getAdminVideoTemplates();
    if (loadingRef.current || loadingMoreRef.current) return;
    if (form.total > 0 && list.length >= form.total) return;
    try {
      setLoadingMore(true);
      await agentCompanyAssetManager.fetchAdminVideoTemplates({
        append: true,
        agentId: agentIdRef.current
      });
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoadingMore(false);
    }
  }, [toast])

  const changeSearchForm = (key: keyof AdminVideoTemplateListSearchForm, value: string | number) => {
    const current = agentCompanyAssetManager.getAdminVideoTemplatesSearchForm();
    agentCompanyAssetManager.setAdminVideoTemplatesSearchForm({
      ...current,
      [key]: value,
    });
  }

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onAdminVideoTemplatesChange((e) => {
      setAdminVideoTemplates(e.detail)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onAdminVideoTemplatesSearchFormChange((e) => {
      setAdminVideoTemplatesSearchForm(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    adminVideoTemplates,
    adminVideoTemplatesSearchForm,
    loading,
    loadingMore,

    search,
    loadMore,
    changeSearchForm,
  }
}

// 后台图文模板列表数据
// agentId 仅在需要按智能体过滤时传入（如用户详情页的图文模板新增弹窗），其他场景不传
const useAdminPostTemplatesData = (agentId?: string) => {
  const toast = useToast();
  const [adminPostTemplates, setAdminPostTemplates] = useState(agentCompanyAssetManager.getAdminPostTemplates());
  const [adminPostTemplatesSearchForm, setAdminPostTemplatesSearchForm] = useState(agentCompanyAssetManager.getAdminPostTemplatesSearchForm());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const agentIdRef = useRef(agentId);
  loadingRef.current = loading;
  loadingMoreRef.current = loadingMore;
  agentIdRef.current = agentId;

  const search = async () => {
    try {
      setLoading(true);
      await agentCompanyAssetManager.fetchAdminPostTemplates({ append: false, agentId: agentIdRef.current });
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const loadMore = useCallback(async () => {
    const form = agentCompanyAssetManager.getAdminPostTemplatesSearchForm();
    const list = agentCompanyAssetManager.getAdminPostTemplates();
    if (loadingRef.current || loadingMoreRef.current) return;
    if (form.total > 0 && list.length >= form.total) return;
    try {
      setLoadingMore(true);
      await agentCompanyAssetManager.fetchAdminPostTemplates({ append: true, agentId: agentIdRef.current });
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoadingMore(false);
    }
  }, [toast])

  const changeSearchForm = (key: keyof AdminPostTemplateListSearchForm, value: string | number) => {
    const current = agentCompanyAssetManager.getAdminPostTemplatesSearchForm();
    agentCompanyAssetManager.setAdminPostTemplatesSearchForm({
      ...current,
      [key]: value,
    });
  }

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onAdminPostTemplatesChange((e) => {
      setAdminPostTemplates(e.detail)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onAdminPostTemplatesSearchFormChange((e) => {
      setAdminPostTemplatesSearchForm(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    adminPostTemplates,
    adminPostTemplatesSearchForm,
    loading,
    loadingMore,

    search,
    loadMore,
    changeSearchForm,
  }
}

// 后台文章模板列表数据
// agentId 仅在需要按智能体过滤时传入（如用户详情页的文章模板新增弹窗），其他场景不传
const useAdminArticleTemplatesData = (agentId?: string) => {
  const toast = useToast();
  const [adminArticleTemplates, setAdminArticleTemplates] = useState(agentCompanyAssetManager.getAdminArticleTemplates());
  const [adminArticleTemplatesSearchForm, setAdminArticleTemplatesSearchForm] = useState(agentCompanyAssetManager.getAdminArticleTemplatesSearchForm());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const agentIdRef = useRef(agentId);
  loadingRef.current = loading;
  loadingMoreRef.current = loadingMore;
  agentIdRef.current = agentId;

  const search = async () => {
    try {
      setLoading(true);
      await agentCompanyAssetManager.fetchAdminArticleTemplates({ append: false, agentId: agentIdRef.current });
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const loadMore = useCallback(async () => {
    const form = agentCompanyAssetManager.getAdminArticleTemplatesSearchForm();
    const list = agentCompanyAssetManager.getAdminArticleTemplates();
    if (loadingRef.current || loadingMoreRef.current) return;
    if (form.total > 0 && list.length >= form.total) return;
    try {
      setLoadingMore(true);
      await agentCompanyAssetManager.fetchAdminArticleTemplates({ append: true, agentId: agentIdRef.current });
    } catch (error) {
      console.error(error)
      toast.showToastByCode(error);
    } finally {
      setLoadingMore(false);
    }
  }, [toast])

  const changeSearchForm = (key: keyof AdminArticleTemplateListSearchForm, value: string | number) => {
    const current = agentCompanyAssetManager.getAdminArticleTemplatesSearchForm();
    agentCompanyAssetManager.setAdminArticleTemplatesSearchForm({
      ...current,
      [key]: value,
    });
  }

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onAdminArticleTemplatesChange((e) => {
      setAdminArticleTemplates(e.detail)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentCompanyAssetManager.onAdminArticleTemplatesSearchFormChange((e) => {
      setAdminArticleTemplatesSearchForm(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    adminArticleTemplates,
    adminArticleTemplatesSearchForm,
    loading,
    loadingMore,

    search,
    loadMore,
    changeSearchForm,
  }
}

export const agentCompanyAssetHooks = {
  useTemplatesData,
  useTemplateAdd,
  useTemplateToggleStatus,
  useTemplateDelete,
  useAdminVideoTemplatesData,
  useAdminPostTemplatesData,
  useAdminArticleTemplatesData,
}

// 导出单例实例
export const agentCompanyAssetManager = AgentCompanyAssetManager.getInstance();
